import {
  UpdateCartData,
  UpdateCartProps,
  useUpdateCart,
} from "src/hooks/useUpdateCart";
import { useCallback, useContext, useEffect, useState } from "react";
import { AppCtx } from "src/contexts/app.context";
import { requestSource, useSegment } from "src/hooks/useSegment";
import { AddressInput } from "src/types/ctgraphql.d";
import { ExecutionResult } from "graphql";
import { ApolloError } from "@apollo/client";
import { ID } from "@ht-sdks/events-sdk-js-browser";

const useUpdateCartWithExpressCheckoutData = ({
  refetchQueries = [],
  onCompleted,
  onError,
}: UpdateCartProps = {}): {
  data: UpdateCartData | undefined;
  updateCartWithExpressCheckoutData: (
    shippingAddress: AddressInput,
    billingAddress: AddressInput,
    customerEmail: string,
    shippingMethodId: string
  ) => Promise<ExecutionResult<UpdateCartData> | undefined>;
  loading: boolean;
  error: ApolloError | undefined;
} => {
  const { storeConfig } = useContext(AppCtx);
  const { user } = useSegment();
  const [anonymousId, setAnonymousId] = useState<ID>("");

  useEffect(() => {
    const getAnonymousId = async () => {
      const htUser = await user;
      setAnonymousId(htUser?.anonymousId?.() || "");
    };
    void getAnonymousId();
  }, [user]);

  const { updateCart, data, loading, error } = useUpdateCart({
    refetchQueries,
    onCompleted,
    onError,
  });

  const updateCartWithExpressCheckoutData = useCallback(
    (
      shippingAddress: AddressInput,
      billingAddress: AddressInput,
      customerEmail: string,
      shippingMethodId: string
    ) =>
      updateCart([
        {
          setLocale: {
            locale: storeConfig.locale,
          },
        },
        {
          setCustomerEmail: {
            email: customerEmail,
          },
        },
        {
          setShippingAddress: {
            address: shippingAddress,
          },
        },
        {
          setBillingAddress: {
            address: billingAddress,
          },
        },
        {
          setCustomField: {
            name: "segment_anonymous_id",
            value: `\"${anonymousId as string}\"`,
          },
        },
        {
          setCustomField: {
            name: "device_type",
            value: `\"${requestSource.request_source}\"`,
          },
        },
        {
          setShippingMethod: {
            shippingMethod: {
              id: shippingMethodId,
            },
          },
        },
      ]),
    [updateCart, storeConfig.locale, anonymousId]
  );

  return { updateCartWithExpressCheckoutData, data, loading, error };
};

export default useUpdateCartWithExpressCheckoutData;
